import React, { useContext, useEffect, useState } from 'react';
import { getKeyValue } from '../../../../utils/stringUtils';
import { getWeeksPast } from '../../../../utils/dateUtils';
import { useNavigate } from 'react-router';

import { useQuery } from '@apollo/client';

import { Chip, Grid } from '@mui/material';

import GeneralLayout from '../../../../layouts/General';
import { AuthContext } from '../../../../components/AuthProvider';
import GridCard from '../../../../components/Grid/GridCard';
import GridActionCard from '../../../../components/Grid/GridActionCard';
import SortableTable, { SortableTableHeader } from '../../../../components/SortableTable';

import { casesInitQuery, Case } from './queries';
import { casePotStatusDescription, getCaseStatus } from '../../../Lab/Cases/CheckIn/Common/case';

const lastWeek = getWeeksPast(1).toISOString();

const Cases = (): React.ReactElement => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  if (authContext === null) {
    throw new Error('No AuthContext');
  }
  const { user } = authContext;
  const [cases, setCases] = useState<Case[]>([]);

  const { data: casesInitData } = useQuery(casesInitQuery(user?.id || '', lastWeek), { fetchPolicy: 'cache-first' });

  useEffect(() => {
    let mounted = true;
    if (mounted && casesInitData) {
      const modifiedCases = casesInitData.cases.map((c: Case) => ({
        ...c,
        status: casePotStatusDescription[getCaseStatus(c.pots.map((p) => p.enum_pot_status.status))],
      }));
      setCases(modifiedCases);
    }
    return () => {
      mounted = false;
    };
  }, [casesInitData]);

  const handleCreate = () => {
    navigate('/org/cases/create');
  };

  const handleView = (id: string) => {
    navigate(`/org/cases/${id}`);
  };

  const handleSearch = () => {
    navigate(`/org/cases/search`);
  };

  const cleanObject = (data: any) => JSON.parse(JSON.stringify(data));

  const tableHeaders: SortableTableHeader[] = [
    { key: 'accession_id', label: 'Accession Id' },
    { key: 'patient.nhs_id', label: 'NHS number' },
    { key: 'mrn', label: 'MRN' },
    { key: 'pots_aggregate.aggregate.count', label: 'Samples' },
    { key: 'lab.name', label: 'Lab' },
    { key: 'enum_case_priority.description', label: 'Priority' },
    { key: 'created_at', label: 'Created at', format: 'DD/MM/YYYY HH:mm' },
    { key: 'status', label: 'Status' },
  ];

  return (
    <GeneralLayout currentPage="Cases">
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12}>
          <Grid container spacing={4} alignItems="stretch">
            <GridActionCard fill gridSize={6} label="Create new case" onClick={handleCreate} />
            <GridCard fill gridSize={6} title="Search cases" description="Search for any case" viewLabel="Search" handleView={() => handleSearch()} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12}>
              <SortableTable
                title="Your cases - 7 days"
                counter={cases.length}
                headers={tableHeaders}
                orderdBy="created_at"
                rows={cases.map((c: Case) => ({
                  key: c.id,
                  actions: [
                    {
                      label: 'View',
                      onClick: () => handleView(c.id),
                    },
                  ],
                  columns: tableHeaders.map((t) => ({
                    key: t.key,
                    format: t.format,
                    label: getKeyValue(c, t.key),
                    component:
                      t.key === 'status' ? (
                        <Chip size="small" label={getKeyValue(c, t.key)} variant="outlined" />
                      ) : t.key === 'enum_case_priority.description' ? (
                        <Chip size="small" label={getKeyValue(c, t.key)} variant="outlined" />
                      ) : undefined,
                  })),
                  collapse: {
                    title: 'Samples',
                    headers: [
                      {
                        key: 'name',
                        label: 'Name',
                      },
                      {
                        key: 'status',
                        label: 'Status',
                      },
                      {
                        key: 'speciality',
                        label: 'Speciality',
                      },
                      ...cleanObject(c.pots[0].pathway).map((pathway: { id: string; title: string }) => ({
                        key: pathway.id,
                        label: pathway.title,
                      })),
                    ],
                    rows: c.pots.map((pot) => ({
                      key: pot.id,
                      columns: [
                        {
                          key: 'name',
                          label: pot.name,
                        },
                        {
                          key: 'status',
                          label: pot.enum_pot_status?.description || 'Initial',
                          component: <Chip size="small" label={pot.enum_pot_status?.description || 'Initial'} variant="outlined" />,
                        },
                        {
                          key: 'speciality',
                          label: `${pot.category.speciality.name}, ${pot.category.name}`,
                        },
                        ...cleanObject(pot.pathway).map((pathway: { id: string; label: string }) => ({
                          key: pathway.id,
                          label: pathway.label,
                        })),
                      ],
                    })),
                  },
                }))}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </GeneralLayout>
  );
};

export default Cases;
